import { lruMemoize } from "@reduxjs/toolkit";
import {
  ComputeRoutesRequest,
  ComputeRoutesResponse,
  PolylineEncoding,
  RouteTravelMode,
  WayPoint,
} from "./@google/types";

const isWayPointEqual = (a: WayPoint, b: WayPoint) => {
  return (
    a.location?.latLng?.latitude === b.location?.latLng?.latitude &&
    a.location?.latLng?.longitude === b.location?.latLng?.longitude
  );
};

/**
 * Fetch the directions for a list of plan items.
 * Aggressively memoized to avoid accidental bugs causing excessive calls and thus a large bill.
 */
const requestDirectionsMemoized = lruMemoize(
  async (params: RequestDirectionsParams) => {
    return requestDirections(params);
  },
  {
    // Deep equality check if request was made previously, will return memoized version if true.
    equalityCheck: (a: RequestDirectionsParams, b: RequestDirectionsParams) => {
      return (
        a.travelMode === b.travelMode &&
        isWayPointEqual(a.origin, b.origin) &&
        isWayPointEqual(a.destination, b.destination) &&
        a.waypoints?.length === b.waypoints?.length &&
        (a.waypoints?.every((waypoint, index) =>
          isWayPointEqual(waypoint, b.waypoints![index]),
        ) ??
          true)
      );
    },
    // Max number of non-equal requests that will be cached.
    maxSize: 3,
  },
);

interface RequestDirectionsParams {
  travelMode: RouteTravelMode;
  origin: WayPoint;
  destination: WayPoint;
  waypoints?: WayPoint[] | undefined;
}

const requestDirections = async (
  params: RequestDirectionsParams,
): Promise<ComputeRoutesResponse> => {
  if (!params.origin || !params.destination) {
    throw new Error("Origin and destination are required");
  }

  const request: ComputeRoutesRequest = {
    origin: params.origin,
    destination: params.destination,
    travelMode: params.travelMode,
    intermediates: params.waypoints,
    optimizeWaypointOrder: false,
    polylineEncoding: PolylineEncoding.GEO_JSON_LINESTRING,
  };

  const response = await fetch(
    "https://routes.googleapis.com/directions/v2:computeRoutes",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": VisitWidget.settings.mapBrowserApiKey,
        "X-Goog-FieldMask":
          "routes,geocodingResults.intermediates.intermediateWaypointRequestIndex",
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    throw new Error(`Directions API request failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
};

export { requestDirectionsMemoized as requestDirections };
